import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import ElapsedTimer from '../ElapsedTimer';
import './KanbanCard.css';

const STATUS_COLORS = {
  pending: '#ff9800',
  preparing: '#2196F3',
  ready: '#A0F487',
  delivered: '#8C92A5',
};

const PLATFORM_COLORS = {
  pedidosya: '#FF0066',
  rappi: '#FF5A00',
  ubereats: '#06C167',
  glovo: '#FFC244',
};

const ORDER_TYPE_CONFIG = {
  dine_in: { icon: '🍽️', color: '#A0F487' },
  takeaway: { icon: '📦', color: '#FF9800' },
  delivery: { icon: '🛵', color: '#FF0066' },
};

const STATUS_FLOW = {
  pending: 'preparing',
  preparing: 'ready',
  ready: 'delivered',
};

const KanbanCard = React.memo(function KanbanCard({ order, onStatusChange, onCardClick, onCancelOrder, isCompact, isDragging, userRole }) {
  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortableDragging } = useSortable({
    id: order.orderId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleCardClick = (e) => {
    // Only open modal if not dragging and clicked on card body (not buttons)
    if (!isSortableDragging && onCardClick && e.target.closest('.kanban-card-body')) {
      onCardClick(order);
    }
  };

  const handleNextStatus = (e) => {
    e.stopPropagation();
    const nextStatus = STATUS_FLOW[order.status];
    if (nextStatus && onStatusChange) {
      onStatusChange(order.orderId, nextStatus);
    }
  };

  const nextStatus = STATUS_FLOW[order.status];

  const isDelivery = order.orderType === 'delivery';
  const platformColor = isDelivery ? PLATFORM_COLORS[order.deliveryPlatform] || '#666' : null;

  // Get order type badge info
  const getOrderTypeBadge = () => {
    if (isDelivery) {
      return {
        icon: '🛵',
        label: order.deliveryPlatform?.toUpperCase() || 'DELIVERY',
        shortLabel: order.deliveryPlatform?.toUpperCase().slice(0, 2) || 'DL',
        color: platformColor,
      };
    }
    if (order.orderType === 'takeaway') {
      return {
        icon: '📦',
        label: t('orders.takeaway', 'Para llevar'),
        shortLabel: 'TK',
        color: ORDER_TYPE_CONFIG.takeaway.color,
      };
    }
    // dine_in or default
    return {
      icon: '🍽️',
      label: `${t('orders.table', 'Mesa')} ${order.tableNumber || '?'}`,
      shortLabel: `M${order.tableNumber || '?'}`,
      color: ORDER_TYPE_CONFIG.dine_in.color,
    };
  };

  const typeBadge = getOrderTypeBadge();

  // Coordination helpers
  const isCoordinated = order.coordinated === true;
  const getCoordinationLabel = () => {
    if (!isCoordinated) return null;
    const ks = order.kitchenStatus;
    const bs = order.barStatus;
    if (ks === 'ready' && bs === 'ready') {
      return { text: t('coordination.readyToServe'), className: 'coordination-ready' };
    }
    if (userRole === 'COMANDA' || userRole === 'OWNER' || userRole === 'ADMIN') {
      if (bs !== 'ready') return { text: t('coordination.waitingBar'), className: 'coordination-waiting' };
    }
    if (userRole === 'BARRA' || userRole === 'OWNER' || userRole === 'ADMIN') {
      if (ks !== 'ready') return { text: t('coordination.waitingKitchen'), className: 'coordination-waiting' };
    }
    return null;
  };

  const coordinationLabel = getCoordinationLabel();

  // Compact card for "delivered" column
  if (isCompact) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`kanban-card kanban-card-compact ${isDragging ? 'kanban-card-dragging' : ''} ${isDelivery ? 'kanban-card-delivery' : ''}`}
      >
        <div className="kanban-card-compact-content">
          <span className="kanban-card-type-badge-compact" style={{ backgroundColor: typeBadge.color }}>
            {typeBadge.icon} {typeBadge.shortLabel}
          </span>
          <span className="kanban-card-time-compact">{formatTime(order.createdAt)}</span>
          <span className="kanban-card-items-count">{t('orders.itemsCount', { count: order.items?.length || 0 })}</span>
        </div>
      </div>
    );
  }

  // Full card
  return (
    <ElapsedTimer
      createdAt={order.createdAt}
      render={(elapsedMinutes) => {
        const isUrgent = elapsedMinutes > 15 && order.status !== 'delivered';
        return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`kanban-card ${isUrgent ? 'kanban-card-urgent' : ''} ${isDragging ? 'kanban-card-dragging' : ''} ${isDelivery ? 'kanban-card-delivery' : ''}`}
    >
      <div className="kanban-card-body" onClick={handleCardClick}>
        {/* Header */}
        <div className="kanban-card-header" style={{ borderLeftColor: typeBadge.color }}>
          <div className="kanban-card-info">
            <span className="kanban-card-type-badge" style={{ backgroundColor: typeBadge.color }}>
              {typeBadge.icon} {typeBadge.label}
            </span>
          </div>
          <div className="kanban-card-time">
            <span className="kanban-card-created">{formatTime(order.createdAt)}</span>
            {elapsedMinutes > 0 && (
              <span className={`kanban-card-elapsed ${isUrgent ? 'urgent' : ''}`}>
                {elapsedMinutes}min
              </span>
            )}
          </div>
        </div>

        {/* Delivery address (if delivery order) */}
        {isDelivery && order.deliveryAddress && (
          <div className="kanban-card-delivery-info">
            <span className="material-icons" aria-hidden="true">location_on</span>
            <span className="kanban-card-address">
              {order.deliveryAddress.street || order.deliveryAddress.formatted || t('orders.delivery.noAddress', 'Sin dirección')}
            </span>
          </div>
        )}

        {/* Payment badge */}
        {order.paymentStatus === 'paid' ? (
          <div className="kanban-card-payment-badge paid">
            <span className="material-icons" aria-hidden="true">check_circle</span>
            {t('orders.paid', 'Pagado')}
          </div>
        ) : order.paymentStatus === 'unpaid' || !order.paymentStatus ? (
          <div className="kanban-card-payment-badge unpaid">
            <span className="material-icons" aria-hidden="true">pending</span>
            {t('orders.unpaid', 'Sin cobrar')}
          </div>
        ) : null}

        {/* Coordinated badge */}
        {isCoordinated && (
          <div className="kanban-card-coordinated">
            <div className="kanban-card-coordinated-badge">
              <span className="material-icons" aria-hidden="true">link</span>
              {t('coordination.coordinated')}
            </div>
            {coordinationLabel && (
              <span className={`kanban-card-coordination-status ${coordinationLabel.className}`}>
                {coordinationLabel.text}
              </span>
            )}
          </div>
        )}

        {/* Items (limited to 3, then show "+X more") */}
        <div className="kanban-card-items">
          {order.items?.slice(0, 3).map((item, idx) => {
            // Support both modifiers and selectedModifiers field names
            const modifiers = item.selectedModifiers || item.modifiers || [];
            return (
              <div key={idx} className="kanban-card-item">
                <div className="kanban-card-item-main">
                  <span className="kanban-card-item-qty">{item.quantity}x</span>
                  <span className="kanban-card-item-name">{item.name}</span>
                  {item.notes && <span className="kanban-card-item-note">*</span>}
                </div>
                {modifiers.length > 0 && (
                  <div className="kanban-card-item-modifiers">
                    {modifiers.map((mod, modIdx) => (
                      <span key={modIdx} className="kanban-card-modifier">
                        + {mod.optionName || mod.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {order.items?.length > 3 && (
            <div className="kanban-card-more">
              +{order.items.length - 3} {t('orders.moreItems', 'more')}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="kanban-card-actions">
        {order.status === 'pending' && onCancelOrder && (
          <button
            className="kanban-card-action kanban-card-action-cancel"
            onClick={(e) => {
              e.stopPropagation();
              onCancelOrder(order);
            }}
            aria-label={t('orders.cancelOrder')}
          >
            <span className="material-icons" aria-hidden="true">delete</span>
          </button>
        )}
        {nextStatus && onStatusChange && (
          <button
            className="kanban-card-action kanban-card-action-next"
            onClick={handleNextStatus}
            style={{ backgroundColor: STATUS_COLORS[nextStatus] }}
          >
            <span className="material-icons" aria-hidden="true">
              {order.status === 'pending' && 'restaurant'}
              {order.status === 'preparing' && 'check_circle'}
              {order.status === 'ready' && 'delivery_dining'}
            </span>
            <span>{t(`orders.action.${order.status}`, getActionLabel(order.status))}</span>
          </button>
        )}
      </div>
    </div>
        );
      }}
    />
  );
});

function getActionLabel(status) {
  const labels = {
    pending: 'Preparar',
    preparing: 'Listo',
    ready: 'Entregar',
  };
  return labels[status] || '';
}

export default KanbanCard;
